import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getBadges, deleteBadge, clearCurrentBadge } from '../../actions/badgeActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import { Plus, Award, Pencil, Eye, Trash2 } from 'lucide-react';
import { isAdminRole } from '../../utils/roleHelpers';

const RARITY_COLORS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700',
  platinum: '#E5E4E2'
};

const TRIGGER_LABELS = {
  course_completion: 'Course Completion',
  lesson_completion: 'Lesson Completion',
  path_completion: 'Path Completion',
  manual: 'Manual Award'
};

const BadgesManagementContainer = ({
  auth: { user },
  badge: { badges, loading },
  getBadges,
  deleteBadge,
  clearCurrentBadge
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    getBadges();
  }, [getBadges]);

  const handleAdd = () => {
    clearCurrentBadge();
    navigate('/content-management/badges/new');
  };

  const handleView = (badge) => navigate(`/content-management/badges/${badge._id}/view`);
  const handleEdit = (badge) => navigate(`/content-management/badges/${badge._id}/edit`);
  const handleDeleteClick = (badge) => { setItemToDelete(badge); setShowDeleteConfirmation(true); };
  const handleDeleteConfirm = () => { deleteBadge(itemToDelete._id); setShowDeleteConfirmation(false); setItemToDelete(null); };
  const handleDeleteCancel = () => { setShowDeleteConfirmation(false); setItemToDelete(null); };

  const handleTabChange = (tabId) => {
    if (tabId === 'Badges') return;
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Badges"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={false}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#054581]">Manage Badges</h2>
              <button
                onClick={handleAdd}
                className="bg-[#1086a4] text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add</span>
              </button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : badges.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Award className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No badges created yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {badges.map(badge => (
                  <div key={badge._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      {badge.image ? (
                        <img
                          src={badge.image}
                          alt={badge.title}
                          className="w-14 h-14 rounded-full object-cover border-3"
                          style={{ borderColor: RARITY_COLORS[badge.rarity], borderWidth: '3px' }}
                        />
                      ) : (
                        <div
                          className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center"
                          style={{ borderColor: RARITY_COLORS[badge.rarity], borderWidth: '3px', borderStyle: 'solid' }}
                        >
                          <Award className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#054581] truncate">{badge.title}</h3>
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                          style={{ backgroundColor: RARITY_COLORS[badge.rarity], color: '#333' }}
                        >
                          {badge.rarity}
                        </span>
                      </div>
                    </div>

                    {badge.description && (
                      <p className="text-gray-500 text-xs mb-2 line-clamp-2">{badge.description}</p>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                      <span>{TRIGGER_LABELS[badge.triggerType]}</span>
                      {badge.triggerTarget && (
                        <span className="truncate ml-2">{badge.triggerTarget.title}</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {badge.awardedCount || 0} awarded
                        {!badge.isActive && <span className="ml-2 text-red-400">(Inactive)</span>}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => handleView(badge)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#1086a4] transition-colors">
                          <Eye size={16} />
                        </button>
                        <button onClick={() => handleEdit(badge)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#1086a4] transition-colors">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => handleDeleteClick(badge)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <DeleteConfirmationModal
              isOpen={showDeleteConfirmation}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              itemTitle={itemToDelete?.title || ''}
              itemType="Badge"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  badge: state.badge
});

export default connect(mapStateToProps, { getBadges, deleteBadge, clearCurrentBadge })(BadgesManagementContainer);
